import { api } from './api-base';

type CacheEntry<T> = { ts: number; promise: Promise<T> };
const inflightCache: Record<string, CacheEntry<any>> = {};
const lastMarkRead: Record<string, number> = {};

const rateLimit = <T>(key: string, windowMs: number, fn: () => Promise<T>): Promise<T> => {
  const now = Date.now();
  const existing = inflightCache[key];
  if (existing && now - existing.ts < windowMs) {
    return existing.promise;
  }
  const promise = fn()
    .then((res) => {
      inflightCache[key] = { ts: Date.now(), promise: Promise.resolve(res) };
      return res;
    })
    .catch((err) => {
      // Clear cache on error so next call can retry immediately
      delete inflightCache[key];
      throw err;
    });
  inflightCache[key] = { ts: now, promise };
  return promise;
};

// Direct Messages API
export const messagesApi = {
  unreadCount: async (): Promise<{ count: number }> => {
    const res = await api.get('/messages/unread-count');
    return res.data;
  },
  conversations: async (): Promise<{ conversations: Array<{ peer: { id: string; username?: string | null; email: string }, last: any; unread: boolean }> }> => {
    return rateLimit('messages:conversations', 10000, async () => {
      const res = await api.get('/messages/conversations');
      return res.data;
    });
  },
  threadWith: async (userId: string): Promise<{ messages: Array<{ id: string; senderId: string; recipientId: string; content: string; createdAt: string; readAt?: string | null }> }> => {
    const res = await api.get(`/messages/with/${userId}`);
    return res.data;
  },
  sendTo: async (userId: string, content: string) => {
    const res = await api.post(`/messages/with/${userId}`, { content });
    return res.data;
  },
  markReadWith: async (userId: string) => {
    const now = Date.now();
    const last = lastMarkRead[userId] || 0;
    if (now - last < 5000) {
      return { skipped: true };
    }
    const res = await api.post(`/messages/with/${userId}/read`);
    lastMarkRead[userId] = Date.now();
    return res.data;
  },
  startByUsername: async (username: string, content: string) => {
    const res = await api.post('/messages/start', { username, content });
    return res.data;
  },
  exportMine: async (): Promise<{ messages: any[] }> => {
    const res = await api.get('/messages/export');
    return res.data as any;
  },
  deleteThreadWith: async (userId: string): Promise<{ deleted: number }> => {
    const res = await api.delete(`/messages/with/${userId}`);
    return res.data as any;
  },
  getBlocks: async (): Promise<{ data: Array<{ blockerId: string; blockedId: string; createdAt: string }> }> => {
    return rateLimit('messages:blocks', 45000, async () => {
      const res = await api.get('/messages/blocks');
      return res.data as any;
    });
  },
  blockUser: async (userId: string): Promise<{ blockedId: string }> => {
    const res = await api.post(`/messages/blocks/${userId}`);
    return res.data as any;
  },
  unblockUser: async (userId: string): Promise<{ unblocked: string }> => {
    const res = await api.delete(`/messages/blocks/${userId}`);
    return res.data as any;
  },
};
